import React from 'react';

import {
  LIST_YOUR_PLACE,
  APOLLO_SUPPLY_LANDING,
  SEARCH,
} from 'ha/constants/pageNames';
import { Language, IntlObject } from 'ha/i18n/types';
import { notEmpty } from 'ha/utils/notEmpty';

import FindPlaceToRentIcon from 'custom-icons/find-a-place-to-rent.svg';
import RentOutYourPlaceIcon from 'custom-icons/rent-out-your-place.svg';
import PayRentOnlineIcon from 'custom-icons/pay-rent-online.svg';
import CollectRentOnlineIcon from 'custom-icons/collect-rent-online.svg';
import BlogIcon from 'custom-icons/blog.svg';
import FAQIconNext from 'custom-icons/faq.svg';
import HelpCenterIconNext from 'custom-icons/help-center.svg';

const CTA_BLACK_LIST = [LIST_YOUR_PLACE, APOLLO_SUPPLY_LANDING, SEARCH];

export const checkIfCTAVisible = (pageName: string, pageCategory?: string) =>
  !(
    CTA_BLACK_LIST.includes(pageName) ||
    CTA_BLACK_LIST.includes(pageCategory || '')
  );

export interface Link {
  icon: React.ReactElement;
  title: string;
  caption: string;
  url: string;
  noFollow: boolean;
}

export interface Menu {
  title: string;
  isNew?: boolean;
  links: Link[];
}

export const getLearnMenuLinks = (intl: IntlObject): Menu[] => {
  const { lang, urlResolver, T } = intl;

  const hasCommunityUrl = urlResolver.hasCommunityUrl(lang);

  const tenantLinks: Link[] = [
    {
      icon: <BlogIcon color="default" fontSize="inherit" />,
      title: T('navbar_learn_tenant_blog_title'),
      caption: T('navbar_learn_tenant_blog_caption'),
      url: hasCommunityUrl
        ? urlResolver.getCommunityUrl(lang)
        : urlResolver.getCommunityUrl(Language.en),
      noFollow: !hasCommunityUrl,
    },
    {
      icon: <FAQIconNext color="default" fontSize="inherit" />,
      title: T('navbar_learn_tenant_faq_title'),
      caption: T('navbar_learn_tenant_faq_caption'),
      url: 'https://answers.housinganywhere.com/en/collections/1445947-tenants',
      noFollow: lang !== Language.en,
    },
  ];

  const hasLandlordsCommunityUrl = urlResolver.hasLandlordsCommunityUrl(lang);
  const hasHelpCenterUrl = urlResolver.hasHelpCenterUrl(lang);

  const landlordLinks: Link[] = [
    hasLandlordsCommunityUrl
      ? {
          icon: <BlogIcon color="default" fontSize="inherit" />,
          title: T('navbar_learn_landlord_blog_title'),
          caption: T('navbar_learn_landlord_blog_caption'),
          url: urlResolver.getLandlordsCommunityUrl(lang),
          noFollow: false,
        }
      : undefined,
    {
      icon: <HelpCenterIconNext color="default" fontSize="inherit" />,
      title: T('navbar_learn_landlord_help_title'),
      caption: T('navbar_learn_landlord_help_caption'),
      url: hasHelpCenterUrl
        ? urlResolver.getHelpCenterUrl(lang)
        : urlResolver.getHelpCenterUrl(Language.en),
      noFollow: !hasHelpCenterUrl,
    },
    {
      icon: <FAQIconNext color="default" fontSize="inherit" />,
      title: T('navbar_learn_landlord_faq_title'),
      caption: T('navbar_learn_landlord_faq_caption'),
      url:
        'https://answers.housinganywhere.com/en/collections/1445961-advertisers',
      noFollow: lang !== Language.en,
    },
  ].filter(notEmpty);

  return [
    {
      title: T('navbar_tenants_title'),
      links: tenantLinks,
    },
    {
      title: T('navbar_landlords_title'),
      links: landlordLinks,
    },
  ];
};

export const getProductsMenuLinks = (intl: IntlObject): Menu[] => {
  const { urlResolver, T } = intl;

  return [
    {
      title: T('navbar_products_marketplace_title'),
      links: [
        {
          icon: <FindPlaceToRentIcon color="default" fontSize="inherit" />,
          title: T('navbar_products_renting_title'),
          caption: T('navbar_products_renting_caption'),
          url: urlResolver.getRenting(),
          noFollow: false,
        },
        {
          icon: <RentOutYourPlaceIcon color="default" fontSize="inherit" />,
          title: T('navbar_products_renting_out_title'),
          caption: T('navbar_products_renting_out_caption'),
          url: urlResolver.getRentingOut(),
          noFollow: false,
        },
      ],
    },
    {
      title: T('navbar_products_payments_title'),
      isNew: true,
      links: [
        {
          icon: <PayRentOnlineIcon color="default" fontSize="inherit" />,
          title: T('navbar_products_online_rent_payment_title'),
          caption: T('navbar_products_online_rent_payment_caption'),
          url: urlResolver.getOnlineRentPayment(),
          noFollow: false,
        },
        {
          icon: <CollectRentOnlineIcon color="default" fontSize="inherit" />,
          title: T('navbar_products_online_rent_collection_title'),
          caption: T('navbar_products_online_rent_collection_caption'),
          url: urlResolver.getPaymentsLandlord(),
          noFollow: false,
        },
      ],
    },
  ];
};
